import { useCallback, useEffect, useRef, useState } from "react";
import ThemeConstants from "../../../../config/theme/theme_constants";
import { RewardInterface } from "../../../home/domain/interface/reward_interface";
import UvibesPointIcon from '../../../../assets/images/uvibes_point.svg?react';
import type { TFunction } from 'i18next';
import { useTranslation } from '../../../../i18n/useTranslation';
import { getCurrentLocale } from '../../../../i18n/config';

const getRewardDate = (t: TFunction, startDate?: Date, endDate?: Date): string => {
    const formatDate = (date: Date): string =>
        date.toLocaleDateString(getCurrentLocale(), { day: 'numeric', month: 'long' });

    if (startDate && endDate) {
        return t('rewards.date_range', { start: formatDate(startDate), end: formatDate(endDate) });
    } else if (startDate) {
        return t('rewards.date_from', { start: formatDate(startDate) });
    } else if (endDate) {
        return t('rewards.date_until', { end: formatDate(endDate) });
    }
    return '';
}

const ExclusiveRewardElement: React.FC<{ reward: RewardInterface, experience: number, onClick: () => void }> = ({ reward, experience, onClick }) => {
    const { t } = useTranslation();
    const [ progress, setProgress ] = useState<number>(0);
    const theme = ThemeConstants();

    useEffect(() => {
        const progress = Math.min(100, (experience / reward.level) * 100);
        setProgress(progress)
    }, [reward, experience]);


    const buttonRef = useRef<HTMLButtonElement>(null);

    const handleOpenDialog = useCallback(() => {
        if (buttonRef.current) {
            buttonRef.current.blur();
        }
        onClick();
    }, [onClick, buttonRef]);

    return (
        <div className="flex flex-col w-45.5 gap-3 cursor-pointer" onClick={handleOpenDialog}>
            <div
                className="flex flex-col justify-between w-full h-30 relative bg-black rounded-[20px] p-2 text-white"
                style={reward.picture ? { 
                    backgroundImage: `url(${reward.picture})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    boxShadow: '0 4px 4px rgba(0, 0, 0, 0.25)',
                } : {
                    boxShadow: '0 4px 4px rgba(0, 0, 0, 0.25)',
                }}
            >
                <div className="absolute top-0 left-0 w-full h-full bg-black opacity-40 rounded-[20px] z-1"/>
                <span className="text-xs py-0.5 px-1.5 rounded-xl w-min z-2" style={{ backgroundColor: theme.UvibesOrange }}>{t('rewards.exceptional')}</span>
                {progress === 100 && !reward.userReward.earned && reward.userReward.id && reward.address ? (
                    <button
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 py-2.5 px-3.75 rounded-[15px] text-sm font-bold z-3 cursor-pointer"
                        style={{ backgroundColor: theme.UvibesOrange }}
                    >
                        {t('rewards.claim')}
                    </button>
                ) : (
                    <div className="w-full px-1 pb-1 flex flex-col items-end z-3 gap-1.25">
                        <div className="flex items-center gap-1.25">
                            <span className="text-sm font-bold">{`${Math.min(reward.level, experience)}/${reward.level}`}</span>
                            <UvibesPointIcon className="h-4.5 w-4.5"/>
                        </div>

                        <div className="relative w-full h-2.5">
                                <div
                                    className="absolute w-full h-2.5 rounded-full bg-white"
                                    style={{
                                        border: `1.5px solid ${theme.UvibesOrange}`,
                                    }}
                                />
                                <div
                                    className={`absolute h-2.5 rounded-full transition-normal duration-300 ease-out`}
                                    style={{
                                        backgroundColor: theme.UvibesOrange,
                                        width: `${progress}%`,
                                        border: progress === 100 ? '1.5px solid white' : 'none',
                                    }}
                                />
                        </div>
                    </div>
                )}
            </div>
            <div className="flex flex-col items-start text-black gap-1">
                <span className="text-sm font-bold">{reward.name}</span>
                <span className="text-xs">{reward.startDate || reward.endDate ? getRewardDate(t, reward.startDate, reward.endDate) : reward.description}</span>
            </div>
        </div>
    );
}

export default ExclusiveRewardElement;